import { useCallback } from 'react'
import { useLocation } from 'wouter'
import { useLingui } from '@lingui/react/macro'
import { useApolloClient, useMutation } from '@apollo/client/react'
import { AdminAPI, getFragment, SharedAPI } from '@guestgoodie/graphql'

import * as Form from '@/features/form'
import { useAuth } from '@/features/auth'

export type ClaimOrganizationProps = {
  slug: string
  secret: string
  acceptedContract: boolean
}

type ClaimOrganizationFn = (props: ClaimOrganizationProps) => Promise<void>

export function useClaimOrganization() {
  const { t } = useLingui()

  const client = useApolloClient()
  const { refresh, switchOrg } = useAuth()

  const [, navigate] = useLocation()
  const [claimOrg] = useMutation(AdminAPI.organization.CLAIM)

  const { handlePromiseCatch, errors } = Form.useErrorHelpers<FormData>({})

  const claim = useCallback<ClaimOrganizationFn>(
    async props => {
      return (
        claimOrg({
          variables: {
            input: props
          }
        })
          // We need to set the new organization on the session so that we can act inside of this organization
          .then(({ data, error }) => {
            if (error) return Promise.reject(error)
            if (!data) return Promise.reject('unknown')

            const org = getFragment(
              SharedAPI.organization.ORGANIZATION_FRAGMENT,
              data.claimOrganization
            )

            return switchOrg(org.slug)
          })
          .then(() => {
            // We don't support multi tenancy caching currently, so let's just dump it all
            client.resetStore()
          })
          .then(() => refresh())
          .then(() => navigate('/goodies'))
          .catch(error =>
            handlePromiseCatch(error, {
              unknownString: t`Error creating organization, please try again, or reach out to our support`
            })
          )
      )
    },
    [claimOrg, switchOrg, refresh, navigate, handlePromiseCatch, client, t]
  )

  return {
    claim,
    errors
  }
}
